import { Component, Input, OnChanges, SimpleChanges, ChangeDetectorRef, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import { Measurement } from '../../services/parser.service';
import { buildRegion, HybridViewMode, RegionMatrices } from '../../../utils/region-utils';
import { HeatmapService, HeatmapParams } from '../../services/heatmap.service';
import { EChartsOption } from 'echarts';
import { RegionDetails, RegionMetrics } from '../../models/chart.models';

export interface RegionNode {
  id: string;
  children: RegionNode[];
  level: number;
}

// Tooltip meta used by smart global tooltip
interface ActiveTooltip {
  regionId: string;
  top: number;
  left: number;
  placement: 'top' | 'bottom';
}

// Straight line segment between parent and child
// A connector can be a simple segment; we will break orthogonal branches into multiple segments
interface ConnectorLine {
  x1: number; y1: number; x2: number; y2: number;
}

@Component({
  selector: 'app-region-tree',
  standalone: true,
  imports: [CommonModule, NgxEchartsModule],
  templateUrl: './region-tree.component.html',
  styleUrls: ['./region-tree.component.scss']
})
export class RegionTreeComponent implements OnChanges, AfterViewInit {

  @Output() regionSelected = new EventEmitter<string[]>(); // Changed to emit an array of strings

  // Base number of nodes per level before scaling starts
  private readonly SCALE_BASE_NODES = 5;
  // Minimum scale factor to ensure readability
  private readonly SCALE_MIN = 0.4;

  @Input() measurements: Measurement[] = [];
  @Input() metricsByRegion: Record<string, RegionMetrics> = {};
  @Input() regionDetailsMap: Record<string, RegionDetails> = {};
  @Input() hybridViewMode: HybridViewMode = 'total-cores';

  regionTree: RegionNode | null = null;
  optionsMap: Record<string, EChartsOption> = {};
  imageMap: Record<string, string> = {};
  treeScale = 1;
  selectedNodeIds: string[] = []; // To track selected nodes
  activeTooltip: ActiveTooltip | null = null;
  connectors: ConnectorLine[] = [];
  containerWidth = 0;
  containerHeight = 0;
  // Visual tuning constants for connector geometry
  private readonly BRANCH_GAP_ABOVE_CHILDREN = 8; // desired gap between spine and children
  private readonly MIN_TRUNK_LEN = 12;    // minimal visible vertical length below parent before branching

  @ViewChild('treeContainer', { static: false }) treeContainerRef?: ElementRef<HTMLElement>;
  @ViewChild('wrapper', { static: false }) wrapperRef?: ElementRef<HTMLElement>;

  constructor(private heatmap: HeatmapService, private cd: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('measurements' in changes || 'hybridViewMode' in changes) {
      this.imageMap = {};
      if (this.measurements && this.measurements.length > 0) {
        this.buildTree(this.measurements);
        this.calculateTreeScale();
        this.buildAllOptions(this.measurements);
        this.selectedNodeIds = []; // Reset selection on new data
        this.scheduleConnectorDraw();
      } else {
        this.regionTree = null;
        this.optionsMap = {};
        this.connectors = [];
      }
    }
  }

  ngAfterViewInit(): void {
    // Initial draw after view ready
    this.scheduleConnectorDraw();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.scheduleConnectorDraw();
    if (this.activeTooltip) {
      // Reposition active tooltip relative to its node
      this.repositionActiveTooltip();
    }
  }

  onNodeClick(regionId: string): void {
    const index = this.selectedNodeIds.indexOf(regionId);
    if (index > -1) {
      // Deselect if already selected
      this.selectedNodeIds.splice(index, 1);
    } else {
      // Add to selection if not already selected
      this.selectedNodeIds.push(regionId);
    }
    this.regionSelected.emit([...this.selectedNodeIds]);
  }

  // ---------------------------------------------------------------------------
  // Tooltip logic (global smart tooltip)
  // ---------------------------------------------------------------------------
  showTooltip(evt: MouseEvent | FocusEvent, regionId: string) {
    const target = evt.currentTarget as HTMLElement | null;
    if (!target || !this.metricsByRegion[regionId] || !this.regionDetailsMap[regionId]) {
      return;
    }
    const rect = target.getBoundingClientRect();
    // provisional size assumptions; refined after detectChanges
    const estimatedWidth = 180;
    const estimatedHeight = 120;
    let placement: 'top' | 'bottom' = 'top';
    let top = rect.top - estimatedHeight - 8 + window.scrollY;
    if (top < window.scrollY + 4) { // not enough room above -> place below
      placement = 'bottom';
      top = rect.bottom + 8 + window.scrollY;
    }
    let left = rect.left + rect.width / 2 - estimatedWidth / 2 + window.scrollX;
    const vw = window.innerWidth;
    left = Math.max(4 + window.scrollX, Math.min(left, vw - estimatedWidth - 4 + window.scrollX));
    this.activeTooltip = { regionId, top, left, placement };
    this.cd.detectChanges();
    // After render refine positioning with actual size
    setTimeout(() => this.refineTooltipPosition(), 0);
  }

  hideTooltip() {
    this.activeTooltip = null;
  }

  private refineTooltipPosition() {
    if (!this.activeTooltip) return;
    const el = document.getElementById('rt-tooltip');
    const triggerNode = document.querySelector(`[data-region-id="${this.activeTooltip.regionId}"]`);
    if (el && triggerNode) {
      const rect = (triggerNode as HTMLElement).getBoundingClientRect();
      const tipRect = el.getBoundingClientRect();
      let top = this.activeTooltip.placement === 'top'
        ? rect.top - tipRect.height - 8 + window.scrollY
        : rect.bottom + 8 + window.scrollY;
      // If still overflowing top, flip
      let placement = this.activeTooltip.placement;
      if (top < window.scrollY + 4) {
        placement = 'bottom';
        top = rect.bottom + 8 + window.scrollY;
      }
      let left = rect.left + rect.width / 2 - tipRect.width / 2 + window.scrollX;
      const vw = window.innerWidth;
      left = Math.max(4 + window.scrollX, Math.min(left, vw - tipRect.width - 4 + window.scrollX));
      this.activeTooltip = { regionId: this.activeTooltip.regionId, top, left, placement };
      this.cd.detectChanges();
    }
  }

  private repositionActiveTooltip() {
    if (!this.activeTooltip) return;
    // reuse refine logic
    this.refineTooltipPosition();
  }

  // ---------------------------------------------------------------------------
  // Connector lines (SVG) replacing CSS pseudo-elements
  // ---------------------------------------------------------------------------
  private scheduleConnectorDraw() {
    requestAnimationFrame(() => this.drawConnectors());
  }

  private drawConnectors() {
    if (!this.treeContainerRef?.nativeElement) return;
    if (!this.regionTree) { this.connectors = []; return; }
    const container = this.treeContainerRef.nativeElement;
    const containerRect = container.getBoundingClientRect();
    this.containerWidth = containerRect.width;
    this.containerHeight = containerRect.height;
    const nodeElements = Array.from(container.querySelectorAll<HTMLElement>('.node-wrapper'));
    const map = new Map<string, HTMLElement>();
    nodeElements.forEach(el => {
      const id = el.getAttribute('data-region-id');
      if (id) map.set(id, el);
    });
    const connectors: ConnectorLine[] = [];
    const traverse = (node: RegionNode | null) => {
      if (!node) return;
      const parentEl = map.get(node.id);
      if (parentEl) {
        const parentRect = parentEl.getBoundingClientRect();
        //console.log(`Region ${node.id} - parentRect:`, parentRect.top, parentRect.height);
        // Collect child anchors first to build a single trunk + spine
        const childAnchors: { x: number; y: number; id: string }[] = [];
        node.children.forEach(child => {
          const childEl = map.get(child.id);
          if (!childEl) return;
          const cr = childEl.getBoundingClientRect();
          const cx = cr.left + cr.width / 2 - containerRect.left;
          const cy = cr.top - containerRect.top; // child top
          childAnchors.push({ x: cx, y: cy, id: child.id });
        });

        if (childAnchors.length > 0) {
          const px = parentRect.left + parentRect.width / 2 - containerRect.left; // parent center X
          const py = parentRect.top - containerRect.top; // parent bottom Y
          const childTopYs = childAnchors.map(c => c.y);
          const minChildTop = Math.min(...childTopYs);

          // Distance available between parent bottom and child top
          const available = minChildTop - py;

          if (childAnchors.length === 1) {
            // Simple case: a single vertical line straight down
            const only = childAnchors[0];
            connectors.push({ x1: px, y1: py, x2: px, y2: only.y });
          } else {
            // Multiple children: build trunk + horizontal spine + stubs
            let junctionY = minChildTop - this.BRANCH_GAP_ABOVE_CHILDREN;
            // Guarantee trunk visibility
            if (junctionY - py < this.MIN_TRUNK_LEN) {
              junctionY = py + Math.min(this.MIN_TRUNK_LEN, Math.max(this.MIN_TRUNK_LEN, available / 2));
            }
            // Never place junction below first child top - 2
            junctionY = Math.min(junctionY, minChildTop - 2);
            // Trunk
            if (junctionY > py) {
              connectors.push({ x1: px, y1: py, x2: px, y2: junctionY });
            }
            // Spine
            const minX = Math.min(...childAnchors.map(c => c.x));
            const maxX = Math.max(...childAnchors.map(c => c.x));
            if (maxX - minX > 0) {
              connectors.push({ x1: minX, y1: junctionY, x2: maxX, y2: junctionY });
            }
            // Stubs
            childAnchors.forEach(ch => {
              connectors.push({ x1: ch.x, y1: junctionY, x2: ch.x, y2: ch.y });
            });
          }
        }

        // Traverse children afterwards
        node.children.forEach(child => traverse(child));
      } else {
        node.children.forEach(ch => traverse(ch));
      }
    };
    traverse(this.regionTree);
    this.connectors = connectors;
    this.cd.detectChanges();
  }

  resetSelection(): void {
    // Define a seleção para ser apenas a região '0'
    this.selectedNodeIds = ['0'];
    // Emite o evento para que o componente pai atualize a visualização
    this.regionSelected.emit([...this.selectedNodeIds]);
  }

  private calculateTreeScale(): void {
    if (!this.regionTree) {
      this.treeScale = 1;
      return;
    }

    const levelCounts = new Map<number, number>();
    const queue: RegionNode[] = [this.regionTree];

    while (queue.length > 0) {
      const node = queue.shift()!;
      levelCounts.set(node.level, (levelCounts.get(node.level) || 0) + 1);
      if (node.children) {
        queue.push(...node.children);
      }
    }

    let maxNodesInLevel = 0;
    for (const count of levelCounts.values()) {
      if (count > maxNodesInLevel) {
        maxNodesInLevel = count;
      }
    }

    // Determine scale based on max nodes in a level
    if (maxNodesInLevel > this.SCALE_BASE_NODES) {
      // Scale factor decreases as node count increases, but not below SCALE_MIN
      this.treeScale = Math.max(
        this.SCALE_MIN,
        this.SCALE_BASE_NODES / maxNodesInLevel
      );
    } else {
      this.treeScale = 1;
    }
  }

  private buildTree(meas: Measurement[]): void {
    const regionIds = this.getRenderableRegionIds(meas);
    
    // Sort to process parents before children
    regionIds.sort((a, b) => a.length - b.length || a.localeCompare(b));

    const nodeMap = new Map<string, RegionNode>();
    let root: RegionNode | null = null;

    regionIds.forEach(id => {
      const level = id.split('.').length - 1;
      const node: RegionNode = { id, children: [], level };
      nodeMap.set(id, node);

      if (id === '0') {
        root = node;
      } else {
        const parentId = id.substring(0, id.lastIndexOf('.'));
        const parentNode = nodeMap.get(parentId);
        if (parentNode) {
          parentNode.children.push(node);
        }
      }
    });

    this.regionTree = root;
  }

  private buildAllOptions(meas: Measurement[]): void {
    this.optionsMap = {};
    const regionIds = this.getRenderableRegionIds(meas);

    regionIds.forEach(regionId => {
      try {
        const region: RegionMatrices = buildRegion(regionId, meas, { hybridViewMode: this.hybridViewMode });
        const effMat = this.heatmap.transpose(region.efficiencies).map(row =>
          row.map(v => v * 100)
        );
        const rows = region.columns.length > 1 ? region.columns.slice(1) : [];
        const cols = region.rows;
        const displayMat = effMat.slice(1);
        const params: HeatmapParams = {
          matrix: displayMat,
          rows,
          columns: cols,
          title: `Region ${regionId}`,
          palette: this.heatmap.yellowOrangePurpleBlackPalette,
          min: 0,
          max: 100,
          unit: '%'
        };
        
        const fullOption = this.heatmap.buildHeatmapOption(params);
        
        const thumbOption = JSON.parse(JSON.stringify(fullOption));
        if (thumbOption.visualMap) {
          thumbOption.visualMap.show = false;
        }
        thumbOption.grid = { top: '10%', bottom: '10%', left: '15%', right: '5%' };

        this.optionsMap[regionId] = thumbOption;
      } catch {
        this.optionsMap[regionId] = {} as EChartsOption;
      }
    });
  }

  private getRenderableRegionIds(meas: Measurement[]): string[] {
    const allRegionIds = Array.from(new Set(meas.map(m => m.region)));
    const renderableIds = allRegionIds.filter(regionId => {
      if (regionId === '0') {
        return true;
      }
      try {
        buildRegion(regionId, meas, { hybridViewMode: this.hybridViewMode });
        return true;
      } catch {
        return false;
      }
    });

    if (!renderableIds.includes('0')) {
      renderableIds.push('0');
    }

    renderableIds.sort((a, b) => a.length - b.length || a.localeCompare(b));
    return renderableIds;
  }

  onChartInit(echartsInstance: any, regionId: string): void {
    setTimeout(() => {
      if (echartsInstance) {
        this.imageMap[regionId] = echartsInstance.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff'
        });
        this.cd.detectChanges();
        this.scheduleConnectorDraw();
      }
    }, 500);
  }

  // trackBy for SVG lines to prevent excessive DOM recycling
  trackLine(_index: number, line: ConnectorLine): string {
    return `${line.x1},${line.y1},${line.x2},${line.y2}`;
  }
}
